import React, { useMemo, useState, useEffect } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { SypacIcon, SypacText } from '@sypac/component-library-react';
import { SortingItemInterface, SortingProps } from './Sorting.interface';
import { SortingItem } from './SortingItem';
import { T, useTranslate } from '@tolgee/react';
import Flag from 'react-world-flags';
import { cn } from '../../lib/utils';

export const Sorting: React.FC<SortingProps> = ({
	options,
	action,
	current,
	label,
	customDefault,
	shouldReset,
	dropDownStyle,
	includeDefault = true,
	forContracts = false,
	contractsDateSectionLabel,
}) => {
	const { t } = useTranslate();

	const dateSectionLabel = contractsDateSectionLabel ?? {
		title: 'Sort by date',
		keyName: 'adminContracts.sortByDate',
	};

	const contractPriceOptions = useMemo(
		() =>
			forContracts
				? options.filter(
						(o) =>
							o?.value?.toLowerCase().includes('price') &&
							o?.title?.toLowerCase() !== 'all',
				  )
				: [],
		[forContracts, options],
	);

	const contractDateOptions = useMemo(
		() =>
			forContracts
				? options.filter(
						(o) =>
							o?.value?.toLowerCase().includes('date') &&
							o?.title?.toLowerCase() !== 'all',
				  )
				: [],
		[forContracts, options],
	);

	const initialSelection: SortingItemInterface = includeDefault
		? current!
		: current ?? options[0];

	const [selected, setSelected] =
		useState<SortingItemInterface>(initialSelection);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	useEffect(() => {
		if (shouldReset) {
			setSelected(includeDefault ? current! : current ?? options[0]);
		}
	}, [shouldReset, current, includeDefault, options]);

	const mergedOptions = useMemo(
		() =>
			includeDefault
				? [
						{
							title: customDefault ? customDefault.title : 'Default',
							value: undefined,
						},
						...options,
				  ]
				: options,
		[includeDefault, customDefault, options],
	);

	const closeDropdown = () => setIsOpen(false);
	const ref = useDetectClickOutside({ onTriggered: closeDropdown });

	return (
		<div ref={ref} className="flex gap-x-3 relative justify-start items-start">
			<div
				className="flex gap-2 items-center cursor-pointer"
				onClick={() => setIsOpen(!isOpen)}
			>
				<SypacText variant="body-regular-medium">
					<p className="text-gray-80 whitespace-nowrap">
						{label ? (
							<T keyName={`sorting.${label.key}`}>{label.title}</T>
						) : (
							<>
								{customDefault ? (
									<T keyName={`sorting.${customDefault.key}`}>
										{customDefault.title}
									</T>
								) : (
									<T keyName="sorting.sortedBy">Sorted by:</T>
								)}
							</>
						)}
					</p>
				</SypacText>

				<div className="flex gap-2">
					<Flag
						className="object-cover rounded z-10 border border-solid border-gray-10 mx-1.5 mt-[1px]"
						code={selected?.value}
						width={24}
						height={16}
					/>
					<SypacText variant="body-regular-medium">
						<p className="text-gray-80 whitespace-nowrap">
							{selected?.title ? (
								<T keyName={`sorting.${selected?.title}`}>{selected?.title}</T>
							) : includeDefault ? (
								<>
									{customDefault ? (
										<T keyName={`sorting.${customDefault.key}`}>
											{customDefault.title}
										</T>
									) : (
										<T keyName="sorting.default">Default</T>
									)}
								</>
							) : null}
						</p>
					</SypacText>
				</div>

				<SypacIcon
					iconName="Alt Arrow Down"
					size="custom"
					width="20px"
					height="10px"
				/>
			</div>

			{isOpen &&
				(forContracts ? (
					<div
						className={cn(
							'flex flex-col absolute top-8 border border-solid border-gray-10 rounded-10 shadow-dropdown bg-white min-w-[240px] w-max z-30 box-border',
							dropDownStyle,
						)}
					>
						{contractPriceOptions.length > 0 && (
							<>
								<div className="px-[3px] py-1">
									<SypacText variant="body-regular-medium">
										<p className="h-[27px] flex items-center text-gray-40 text-[12px] leading-[14px] px-1">
											<T keyName="adminContracts.sortByPrice">Sort by price</T>
										</p>
									</SypacText>

									<div className="flex flex-col gap-[2px]">
										{contractPriceOptions.map(({ title, value }) => (
											<SortingItem
												key={`${value}-price`}
												title={t(`sorting.${title.toLowerCase()}`, title)}
												value={value}
												action={(item) => {
													setSelected(item);
													action(item);
													setIsOpen(false);
												}}
												flag={value}
												isSelected={selected?.value === value}
											/>
										))}
									</div>
								</div>
								<div className="border-0 border-solid border-t-[1px] border-gray-10" />
							</>
						)}

						<div className="px-[3px] py-1">
							<SypacText variant="body-regular-medium">
								<p className="h-[27px] flex items-center text-gray-40 text-[12px] leading-[14px] px-1">
									<T keyName={dateSectionLabel.keyName}>
										{dateSectionLabel.title}
									</T>
								</p>
							</SypacText>

							<div className="flex flex-col gap-[2px]">
								{contractDateOptions.map(({ title, value }) => (
									<SortingItem
										key={`${value}-date`}
										title={t(`sorting.${title.toLowerCase()}`, title)}
										value={value}
										action={(item) => {
											setSelected(item);
											action(item);
											setIsOpen(false);
										}}
										flag={value}
										isSelected={selected?.value === value}
									/>
								))}
							</div>
						</div>
					</div>
				) : (
					<div
						className={cn(
							'flex flex-col gap-1 absolute top-8 p-[3px] border border-solid border-gray-10 rounded-10 shadow-dropdown bg-white min-w-[145px] w-max z-30',
							dropDownStyle,
						)}
					>
						{mergedOptions.map(({ title, value }) => {
							return (
								<SortingItem
									key={Math.random()}
									title={t(`sorting.${title.toLowerCase()}`, title)}
									value={value}
									action={(item) => {
										setSelected(item);
										action(item);
										setIsOpen(false);
									}}
									flag={value}
									isSelected={selected?.value === value}
								/>
							);
						})}
					</div>
				))}
		</div>
	);
};
